import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  FileText, Shield, ArrowRight, AlertTriangle, 
  CheckCircle, Users, TrendingUp, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const CaseStudies = () => {
  const caseStudies = [
    {
      id: 'investment-scam-recovery',
      title: 'Investment Scam Investigation',
      category: 'Investment Fraud',
      description: 'Client lost £45,000 to a fake cryptocurrency trading platform. Our investigation traced the fraudulent company structure and prepared evidence for bank dispute.',
      outcome: 'Bank dispute documentation prepared',
      amount: '£45,000',
      icon: TrendingUp,
      color: 'orange'
    },
    {
      id: 'romance-scam-exposure',
      title: 'Romance Scam Investigation',
      category: 'Romance Fraud',
      description: 'Victim targeted through social media by overseas scammers using stolen photos. Our report exposed the network and assisted police reporting.',
      outcome: 'Scam network identified',
      amount: '£12,000',
      icon: Users,
      color: 'pink'
    },
    {
      id: 'fake-company-verification',
      title: 'Company Verification Case',
      category: 'Business Fraud',
      description: 'Business client asked us to verify a new supplier before committing to a large order. Our due diligence revealed the company was not legitimate.',
      outcome: 'Fraud prevented',
      amount: '£80,000',
      icon: Shield,
      color: 'blue'
    },
    {
      id: 'crypto-wallet-trace',
      title: 'Cryptocurrency Fraud Tracing',
      category: 'Crypto Fraud',
      description: 'Client sent funds to a fraudulent DeFi platform. We traced wallet movements and prepared documentation for exchange requests.',
      outcome: 'Wallet trail documented',
      amount: '£23,000',
      icon: Lock,
      color: 'purple'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
      pink: { text: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30' },
      blue: { text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
      purple: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-[#0a1628] pt-28 pb-16">
      <Helmet>
        <title>Case Studies | ActionFraudOne</title>
        <meta name="description" content="Real fraud investigation case studies from ActionFraudOne. See how we help victims of scams with professional investigation services." />
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Case Studies
          </h1>
          <p className="text-gray-400 text-lg">
            Real examples of how our fraud intelligence and investigation services 
            have helped individuals and businesses protect themselves from scams.
          </p>
        </div>

        {/* Case Study Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {caseStudies.map((study) => {
            const colors = getColorClasses(study.color);
            return (
              <Card key={study.id} className={`bg-[#0d1d32] border-[#1e40af]/30 hover:border-[#1e40af]/50 transition-all`}>
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <study.icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div>
                      <span className={`text-xs font-semibold ${colors.text} uppercase tracking-wide`}>
                        {study.category}
                      </span>
                      <h3 className="text-xl font-bold text-white mt-1">{study.title}</h3>
                    </div>
                  </div>
                  
                  <p className="text-gray-500 mb-6 leading-relaxed">{study.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 p-4 bg-[#0a1628] rounded-lg">
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Amount Involved</p>
                      <p className={`text-xl font-bold ${colors.text}`}>{study.amount}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Outcome</p>
                      <p className="text-white font-semibold">{study.outcome}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Statistics */}
        <div className="max-w-4xl mx-auto mb-16">
          <Card className="bg-[#0d1d32] border-[#1e40af]/30">
            <CardContent className="p-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div>
                  <p className="text-4xl font-bold text-blue-400 mb-2">500+</p>
                  <p className="text-gray-500">Cases Investigated</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-cyan-400 mb-2">£2.4M</p>
                  <p className="text-gray-500">Fraud Documented</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-green-400 mb-2">94%</p>
                  <p className="text-gray-500">Client Satisfaction</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-purple-400 mb-2">48hr</p>
                  <p className="text-gray-500">Avg Response Time</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto">
          <Card className="bg-gradient-to-r from-[#1e40af]/20 via-cyan-500/10 to-[#1e40af]/20 border-[#1e40af]/30">
            <CardContent className="p-10 text-center">
              <FileText className="w-14 h-14 text-blue-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Need Help With a Fraud Case?</h3>
              <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                Our team of investigators can help document your case and prepare evidence 
                for banks, police, or legal proceedings.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/investigations">
                  <Button className="bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold px-8 py-6 text-lg">
                    View Investigation Services <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/447386668966" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                    Contact Us
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Disclaimer */}
        <div className="max-w-2xl mx-auto mt-12 text-center">
          <p className="text-gray-600 text-sm">
            <AlertTriangle className="w-4 h-4 inline-block mr-1" />
            Case studies are anonymized examples based on real investigations. 
            Specific details have been changed to protect client confidentiality.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CaseStudies;
